/**
 * GrandLivreTiers.jsx
 * Grand Livre détaillé par tiers (clients / fournisseurs).
 * Chaque compte auxiliaire 411xxx / 401xxx affiche ses mouvements
 * avec solde cumulé : complément du Grand Livre général par compte.
 *
 * Filtres : type (Clients / Fournisseurs / Tous), recherche nom ou compte.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useGetGrandLivreTiersQuery } from 'actions/comptabilite/queries';
import { exportGrandLivreTiersExcel, comparerComptes } from './utils/exportComptaExcel';
import {
  exportButtonGroupStyle,
  exportExcelButtonStyle,
} from './utils/exportButtonStyles';
import TableLoader from 'components/Loader/TableLoader';

// ── Tokens (alignés sur GrandLivre.jsx) ──────────────────────────────
const C = {
  bg:        '#f0f5f6',
  surface:   '#ffffff',
  border:    '#c8d8db',
  borderSoft:'#e2eced',
  teal:      'rgba(00,96,88,0.8)',
  tealDark:  '#006b78',
  tealDim:   'rgba(0,143,160,0.09)',
  green:     '#1a7a52',
  greenDim:  'rgba(26,122,82,0.10)',
  red:       '#c93535',
  redDim:    'rgba(201,53,53,0.10)',
  blueDim:   'rgba(31,111,191,0.10)',
  text:      '#0d2226',
  textDim:   '#3d6068',
  textMuted: '#7da5ad',
  white:     '#ffffff',
  mono:      '"DM Mono","Courier New",monospace',
  display:   '"Playfair Display",Georgia,serif',
  sans:      '"DM Sans",system-ui,sans-serif',
};

const injectFonts = () => {
  if (document.getElementById('gl-fonts')) return;
  const l = document.createElement('link');
  l.id   = 'gl-fonts';
  l.rel  = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600&display=swap';
  document.head.appendChild(l);
};

const fmt = (n) =>
  n && n !== 0 ? new Intl.NumberFormat('fr-FR').format(n) + ' F' : '-';

const thStyle = (align = 'left') => ({
  fontFamily:      C.mono,
  fontSize:        '10px',
  fontWeight:      600,
  letterSpacing:   '1.5px',
  textTransform:   'uppercase',
  color:           C.white,
  backgroundColor: C.teal,
  padding:         '10px 12px',
  textAlign:       align,
  borderBottom:    '2px solid ' + C.tealDark,
  whiteSpace:      'nowrap',
});

const tdStyle = (align = 'left') => ({
  fontFamily:  C.sans,
  fontSize:    '12px',
  color:       C.text,
  padding:     '8px 12px',
  textAlign:   align,
  borderBottom: '1px solid ' + C.borderSoft,
});

const S = {
  wrapper: { fontFamily: C.sans, background: C.bg, color: C.text, padding: '28px 20px 60px', minHeight: '100vh' },
  inner:   { maxWidth: '1160px', margin: '0 auto' },
  headerRow: { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '6px' },
  title:   { fontFamily: C.display, fontSize: '1.6rem', fontWeight: 700, color: C.text, margin: 0 },
  badge: {
    fontFamily: C.mono, fontSize: '9px', letterSpacing: '2px', textTransform: 'uppercase',
    color: C.teal, background: C.tealDim,
    border: '1px solid rgba(0,143,160,0.25)', padding: '3px 12px', borderRadius: '100px',
  },
  divider: { height: '1px', background: 'linear-gradient(90deg,' + C.border + ',transparent)', marginTop: '8px', marginBottom: '20px' },
  filterBar: {
    display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center',
    background: C.surface, border: '1px solid ' + C.border, borderRadius: '10px',
    padding: '14px 18px', marginBottom: '24px', boxShadow: '0 1px 6px rgba(0,143,160,0.06)',
  },
  filterLabel: { fontFamily: C.mono, fontSize: '9px', letterSpacing: '2px', textTransform: 'uppercase', color: C.textMuted, marginRight: '2px' },
  filterInput: {
    fontFamily: C.sans, fontSize: '13px', color: C.text,
    background: C.bg, border: '1px solid ' + C.border,
    borderRadius: '6px', padding: '6px 10px', outline: 'none',
  },
  filterSelect: {
    fontFamily: C.sans, fontSize: '12px', color: C.text,
    background: C.bg, border: '1px solid ' + C.border,
    borderRadius: '6px', padding: '6px 10px', outline: 'none', cursor: 'pointer',
  },
  countBadge: {
    fontFamily: C.mono, fontSize: '10px', color: C.textMuted,
    background: C.tealDim, border: '1px solid rgba(0,143,160,0.2)',
    padding: '3px 10px', borderRadius: '100px',
  },
  card: {
    background: C.surface, border: '1px solid ' + C.border,
    borderRadius: '10px', overflow: 'hidden',
    boxShadow: '0 2px 14px rgba(0,143,160,0.07)', marginBottom: '14px',
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', backgroundColor: C.teal, cursor: 'pointer',
  },
  cardTitle: { fontFamily: C.display, fontWeight: 700, fontSize: '14px', color: C.white },
  cardSub:   { fontFamily: C.mono, fontSize: '10px', color: 'rgba(255,255,255,0.75)', letterSpacing: '1px' },
  typeBadge: {
    fontFamily: C.mono, fontSize: '9px', letterSpacing: '1px', textTransform: 'uppercase',
    color: C.white, background: 'rgba(255,255,255,0.18)',
    padding: '2px 10px', borderRadius: '100px', marginLeft: '10px',
  },
  soldeCell: (nature) => ({
    fontFamily: C.mono, fontSize: '12px', fontWeight: 600, textAlign: 'right',
    color: nature === 'D' ? C.green : nature === 'C' ? C.red : C.textMuted,
    padding: '8px 12px',
  }),
  empty: {
    textAlign: 'center', padding: '60px 20px', color: C.textMuted,
    fontFamily: C.sans, fontSize: '14px',
  },
};

export default function GrandLivreTiers({ dossier_id, date_debut, date_fin }) {
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [openTiers, setOpenTiers] = useState({});

  useEffect(() => { injectFonts(); }, []);

  const { data, isLoading, isError } = useGetGrandLivreTiersQuery(
    { dossier_id, date_debut, date_fin, type: typeFilter || undefined },
    { skip: !dossier_id }
  );

  const tiers = useMemo(() => {
    const list = data?.tiers || [];
    const sorted = [...list].sort(
      (a, b) => comparerComptes(a.compte, b.compte)
    );
    if (!search.trim()) return sorted;
    const q = search.trim().toLowerCase();
    return sorted.filter(
      (t) =>
        String(t.compte).includes(q) ||
        (t.nom || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const handleExportExcel = () => {
    exportGrandLivreTiersExcel({
      tiers,
      dossierId: dossier_id,
      dateDebut: date_debut,
      dateFin: date_fin,
    });
  };

  const toggle = (code) =>
    setOpenTiers((prev) => ({ ...prev, [code]: !prev[code] }));

  if (isLoading) {
    return (
      <div style={S.wrapper}>
        <div style={S.inner}>
          <div style={{ ...S.card, padding: '30px' }}>
            <TableLoader
              message="Chargement du grand livre des tiers..."
              subMessage="Récupération des mouvements clients et fournisseurs"
              minHeight="350px"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.wrapper}>
      <div style={S.inner}>
        <div style={S.headerRow}>
          <h2 style={S.title}>Grand Livre Tiers</h2>
          <span style={S.badge}>Clients &amp; Fournisseurs</span>
          <div style={{ ...exportButtonGroupStyle, marginLeft: 'auto' }}>
            <button
              type="button"
              onClick={handleExportExcel}
              style={exportExcelButtonStyle}
              title="Exporter le grand livre tiers sur la période filtrée"
            >
              Export Excel
            </button>
          </div>
        </div>
        <div style={S.divider} />

        <div style={S.filterBar}>
          <span style={S.filterLabel}>Type</span>
          <select
            style={S.filterSelect}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Tous</option>
            <option value="CLIENT">Clients</option>
            <option value="FOURNISSEUR">Fournisseurs</option>
          </select>

          <span style={{ ...S.filterLabel, marginLeft: '12px' }}>Recherche</span>
          <input
            style={S.filterInput}
            placeholder="Nom ou n° de compte…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <span style={{ ...S.countBadge, marginLeft: 'auto' }}>
            {tiers.length} tiers
          </span>
        </div>

        {isError && (
          <div style={S.empty}>
            Erreur lors du chargement du Grand Livre tiers.
          </div>
        )}

        {!isError && tiers.length === 0 && (
          <div style={S.empty}>
            Aucun mouvement tiers sur la période.
          </div>
        )}

        {tiers.map((t) => {
          const open = !!openTiers[t.compte];
          return (
            <div key={t.compte} style={S.card}>
              <div style={S.cardHeader} onClick={() => toggle(t.compte)}>
                <div>
                  <span style={S.cardTitle}>{t.nom}</span>
                  <span style={S.typeBadge}>{t.type}</span>
                  <div style={S.cardSub}>Compte {t.compte}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ ...S.cardSub, marginBottom: '2px' }}>Solde</div>
                    <div
                      style={{
                        fontFamily: C.mono, fontSize: '13px', fontWeight: 600, color: C.white,
                      }}
                    >
                      {t.nature_solde === 'D' && fmt(t.solde_debiteur) + ' D'}
                      {t.nature_solde === 'C' && fmt(t.solde_crediteur) + ' C'}
                      {t.nature_solde === 'NUL' && 'Soldé'}
                    </div>
                  </div>
                  <span style={{ color: C.white, fontSize: '18px' }}>
                    {open ? '▾' : '▸'}
                  </span>
                </div>
              </div>

              {open && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thStyle('left')}>Date</th>
                        <th style={thStyle('left')}>Libellé</th>
                        <th style={thStyle('left')}>Contrepartie</th>
                        <th style={thStyle('right')}>Débit</th>
                        <th style={thStyle('right')}>Crédit</th>
                        <th style={thStyle('right')}>Solde cumulé</th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.mouvements.map((m, i) => (
                        <tr key={i}>
                          <td style={tdStyle('left')}>{m.date}</td>
                          <td style={tdStyle('left')}>{m.libelle}</td>
                          <td style={{ ...tdStyle('left'), color: C.textMuted }}>{m.contrepartie}</td>
                          <td style={{ ...tdStyle('right'), fontFamily: C.mono }}>{fmt(m.debit)}</td>
                          <td style={{ ...tdStyle('right'), fontFamily: C.mono }}>{fmt(m.credit)}</td>
                          <td
                            style={{
                              ...tdStyle('right'),
                              fontFamily: C.mono,
                              color: m.solde_cumule >= 0 ? C.green : C.red,
                            }}
                          >
                            {fmt(Math.abs(m.solde_cumule))}
                            {m.solde_cumule < 0 ? ' C' : ''}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={3} style={{ ...tdStyle('left'), fontWeight: 600 }}>
                          Total
                        </td>
                        <td style={{ ...tdStyle('right'), fontFamily: C.mono, fontWeight: 600 }}>
                          {fmt(t.total_debit)}
                        </td>
                        <td style={{ ...tdStyle('right'), fontFamily: C.mono, fontWeight: 600 }}>
                          {fmt(t.total_credit)}
                        </td>
                        <td style={S.soldeCell(t.nature_solde)}>
                          {t.nature_solde === 'D' && fmt(t.solde_debiteur)}
                          {t.nature_solde === 'C' && fmt(t.solde_crediteur)}
                          {t.nature_solde === 'NUL' && '-'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
